import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2, Download, Home, Share2, Copy, Mail, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import CVPreview from "@/components/CVPreview";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reference = searchParams.get("reference");
  const [verified, setVerified] = useState(false);
  const [loading, setLoading] = useState(true);
  const [paymentData, setPaymentData] = useState(null);
  const [shareToken, setShareToken] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [cvData, setCvData] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [emailAddress, setEmailAddress] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showEmailInput, setShowEmailInput] = useState(false);

  useEffect(() => {
    if (!reference) {
      navigate("/");
      return;
    }

    const verifyPayment = async () => {
      try {
        const response = await axios.get(`${API}/payment/verify/${reference}`);
        if (response.data.data.verified) {
          setVerified(true);
          setPaymentData(response.data.data);
          setShareToken(response.data.data.shareToken);
          setCvData(response.data.data.cvData);
          toast.success("Payment verified! You can now download your CV.");
        } else {
          toast.error("Payment verification failed.");
        }
      } catch (error) {
        toast.error("Failed to verify payment.");
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    verifyPayment();
  }, [reference, navigate]);

  // Direct download from backend
  const handleDirectDownload = async () => {
    if (!reference) {
      toast.error("Payment reference not found");
      return;
    }

    try {
      setDownloading(true);
      toast.info("Preparing your CV for download...");
      
      // Create a hidden link to trigger download
      const downloadUrl = `${API}/cv/download/${reference}`;
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success("Download started! Check your Downloads folder.");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  // Send CV via email
  const handleSendEmail = async () => {
    if (!emailAddress || !emailAddress.includes('@')) {
      toast.error("Please enter a valid email address");
      return;
    }

    try {
      setSendingEmail(true);
      toast.info("Sending CV to your email...");
      
      const response = await axios.post(`${API}/cv/send-email`, {
        reference: reference,
        recipient_email: emailAddress
      });
      
      if (response.data.status === "success") {
        toast.success(`CV sent to ${emailAddress}!`);
        setShowEmailInput(false);
        setEmailAddress("");
      }
    } catch (error) {
      console.error("Email error:", error);
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  const copyShareLink = () => {
    if (!shareToken) {
      toast.error("Share link not available");
      return;
    }
    
    const shareUrl = `${window.location.origin}/cv/share/${shareToken}`;
    
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(shareUrl)
        .then(() => {
          setCopySuccess(true);
          toast.success("Share link copied to clipboard!");
          setTimeout(() => setCopySuccess(false), 2000);
        })
        .catch(() => {
          fallbackCopyMethod(shareUrl);
        });
    } else {
      fallbackCopyMethod(shareUrl);
    }
  };

  const fallbackCopyMethod = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    document.body.appendChild(textArea);
    textArea.select();
    
    try {
      document.execCommand('copy');
      setCopySuccess(true);
      toast.success("Share link copied!");
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      toast.info(`Copy this link: ${text}`, { duration: 10000 });
    }
    
    document.body.removeChild(textArea);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Verifying payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-6 py-24">
        <div className="text-center space-y-8">
          {verified ? (
            <>
              <div className="flex justify-center">
                <CheckCircle2 className="w-20 h-20 text-accent" strokeWidth={1.5} />
              </div>
              <div className="space-y-4">
                <h1 className="font-heading text-4xl sm:text-5xl font-light">
                  Payment <span className="font-semibold">Successful</span>
                </h1>
                <p className="text-lg text-muted-foreground">
                  Your CV is ready. Download it or send it to your email.
                </p>
              </div>

              <div className="bg-card border border-border/40 shadow-sm rounded-none p-8 max-w-md mx-auto">
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Reference:</span>
                    <span className="font-medium">{reference}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Amount:</span>
                    <span className="font-medium">${paymentData?.amount?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Email:</span>
                    <span className="font-medium">{paymentData?.email}</span>
                  </div>
                </div>
              </div>

              {/* Download Options */}
              <div className="flex flex-col gap-4 max-w-md mx-auto">
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    data-testid="download-cv-btn"
                    onClick={handleDirectDownload}
                    disabled={downloading}
                    className="flex-1 bg-accent text-accent-foreground hover:bg-accent/90 rounded-none h-12 px-6 font-medium"
                  >
                    {downloading ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="mr-2 h-4 w-4" />
                    )}
                    Download CV
                  </Button>
                </div>

                {/* Email Option */}
                {!showEmailInput ? (
                  <Button
                    onClick={() => setShowEmailInput(true)}
                    variant="outline"
                    className="border-border rounded-none h-12"
                  >
                    <Mail className="mr-2 h-4 w-4" />
                    Send to Email
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      placeholder="Enter email address"
                      value={emailAddress}
                      onChange={(e) => setEmailAddress(e.target.value)}
                      className="flex-1 rounded-none h-12"
                      data-testid="email-input"
                    />
                    <Button
                      onClick={handleSendEmail}
                      disabled={sendingEmail}
                      className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-none h-12 px-6"
                      data-testid="send-email-btn"
                    >
                      {sendingEmail ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        "Send"
                      )}
                    </Button>
                  </div>
                )}
              </div>

              {/* Share and Home buttons */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                {shareToken && (
                  <Button
                    data-testid="copy-share-link-btn"
                    onClick={copyShareLink}
                    variant="outline"
                    className="border-border rounded-none h-12 px-8"
                  >
                    {copySuccess ? (
                      <>
                        <CheckCircle2 className="mr-2 h-4 w-4 text-accent" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Share2 className="mr-2 h-4 w-4" />
                        Copy Share Link
                      </>
                    )}
                  </Button>
                )}
                <Button
                  data-testid="home-btn"
                  onClick={() => navigate("/")}
                  variant="outline"
                  className="border-border rounded-none h-12 px-8"
                >
                  <Home className="mr-2 h-4 w-4" />
                  Back to Home
                </Button>
              </div>

              {shareToken && (
                <div className="bg-secondary/30 p-4 border border-border/40 rounded-none max-w-md mx-auto">
                  <p className="text-sm text-muted-foreground mb-2">
                    <Share2 className="inline h-4 w-4 mr-1" />
                    Share your CV with potential employers!
                  </p>
                  <div className="flex gap-2 items-center">
                    <input
                      type="text"
                      readOnly
                      value={`${window.location.origin}/cv/share/${shareToken}`}
                      onClick={(e) => e.target.select()}
                      className="flex-1 bg-card px-3 py-2 text-xs border border-border rounded-none"
                    />
                    <Button
                      onClick={copyShareLink}
                      variant="ghost"
                      size="sm"
                      className="rounded-none"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <h1 className="font-heading text-4xl font-light">Payment Verification Failed</h1>
              <p className="text-muted-foreground">Please contact support or try again.</p>
              <Button onClick={() => navigate("/")} className="rounded-none">
                Back to Home
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuccessPage;
